import { useMemo } from "react";

import { type DispatchTableRow, unacknowledgedRowIds } from "./dispatchTableRow";
import type { TaskFold } from "./tasks";

/** The two counts the page shows once it has tasks. */
export type TaskCounts = {
  /** Everything waiting on the team's drivers: their unacknowledged items and the open tasks about them. */
  open: number;
  /** What waits on this viewer: their view's unacknowledged rows and their board's shelf. */
  mine: number;
};

/**
 * The rows waiting on the viewer, one id each: the view's rows showing the red dot, and every row on the shelf.
 *
 * @param rows - The view's rows after its filters and search, before grouping.
 * @param shelfRows - The board's task shelf; a row also among `rows` counts once.
 */
export function mineIds(rows: readonly DispatchTableRow[], shelfRows: readonly DispatchTableRow[]): string[] {
  return [...new Set([...unacknowledgedRowIds(rows), ...shelfRows.map((row) => row.id)])];
}

/**
 * The open and mine counts, from the per-channel counts, the fold, the view's rows, and the shelf.
 *
 * "Open" counts over the drivers the page lists, as the per-channel counts do: a task about a driver the team does
 * not list is on none of its shelves, so it is not counted either.
 *
 * @param unacknowledgedByFeed - How many items wait on each of the team's channels.
 * @param fold - The workspace's open tasks.
 * @param listedDriverIds - The membership ids of the drivers the page lists.
 * @param rows - The view's rows after its filters and search, before grouping.
 * @param shelfRows - The board's task shelf.
 */
export function taskCounts(
  unacknowledgedByFeed: ReadonlyMap<string, number>,
  fold: TaskFold,
  listedDriverIds: ReadonlySet<string>,
  rows: readonly DispatchTableRow[],
  shelfRows: readonly DispatchTableRow[],
): TaskCounts {
  let items = 0;
  for (const count of unacknowledgedByFeed.values()) items += count;
  const listedTasks = fold.open.filter(({ task }) =>
    task.subjects.some(
      ({ workspaceMembershipId }) => workspaceMembershipId !== null && listedDriverIds.has(workspaceMembershipId),
    ),
  );
  return { open: items + listedTasks.length, mine: mineIds(rows, shelfRows).length };
}

/**
 * The page's split counts: "open" for the team's drivers, "mine" for the viewer, which is what the badge shows.
 *
 * @param unacknowledgedByFeed - How many items wait on each channel.
 * @param fold - The workspace's open tasks.
 * @param listedDriverIds - The membership ids of the drivers the page lists.
 * @param rows - The view's rows after its filters and search, before grouping.
 * @param shelfRows - The board's task shelf.
 */
export function useTaskCounts(
  unacknowledgedByFeed: ReadonlyMap<string, number>,
  fold: TaskFold,
  listedDriverIds: ReadonlySet<string>,
  rows: readonly DispatchTableRow[],
  shelfRows: readonly DispatchTableRow[],
): TaskCounts {
  return useMemo(
    () => taskCounts(unacknowledgedByFeed, fold, listedDriverIds, rows, shelfRows),
    [unacknowledgedByFeed, fold, listedDriverIds, rows, shelfRows],
  );
}
